import { useTranslation } from 'next-i18next';
import { useState } from 'react';

// safely compare password
const equals = (test: string, secret: string): boolean => {
  if (typeof test !== 'string' || typeof secret !== 'string') {
    return false;
  }

  let diffs = test.length !== secret.length ? 1 : 0;
  for (let i = 0; i < test.length; i++) {
    if (test[i] !== secret[i]) {
      diffs++;
    }
  }
  return diffs === 0;
};

interface ValidationPreForm {
  maskPassword: 'password' | 'text';
  maskConfirmPassword: 'password' | 'text';
  checkConditions: boolean;
  errorCredentials: boolean;
  errorLength8: boolean;
  errorNumber1: boolean;
  errorSpecialChar1: boolean;
  setCheckConditions: (value: boolean) => void;
  setErrorCredentials: (value: boolean) => void;
  handleMaskPassword: () => void;
  handleMaskConfirmPassword: () => void;
  validateEmail: (value: string) => string | undefined;
  validatePassword: (value: string) => string | undefined;
  isNewEmail: (meEmail: string | undefined, value: string) => string | undefined;
  isNewPhoneNumber: (mePhoneNumber: string | undefined, value: string) => string | undefined;
  validateConfirmPassword: (password: string, value: string | undefined) => string | undefined;
}

const useValidationPreForm = (): ValidationPreForm => {
  const { t } = useTranslation();

  const [maskPassword, setMaskPassword] = useState<'password' | 'text'>('password');
  const [maskConfirmPassword, setMaskConfirmPassword] = useState<'password' | 'text'>('password');
  const [checkConditions, setCheckConditions] = useState<boolean>(false);
  const [errorCredentials, setErrorCredentials] = useState<boolean>(false);
  const [errorLength8, setErrorLength8] = useState<boolean>(true);
  const [errorNumber1, setErrorNumber1] = useState<boolean>(true);
  const [errorSpecialChar1, setErrorSpecialChar1] = useState<boolean>(true);

  const handleMaskPassword = () => {
    setMaskPassword(prev => (prev === 'text' ? 'password' : 'text'));
  };

  const handleMaskConfirmPassword = () => {
    setMaskConfirmPassword(prev => (prev === 'text' ? 'password' : 'text'));
  };

  const validateEmail = (value: string) => {
    let error;
    if (!value) {
      error = 'Required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value)) {
      error = t('pages.loginRegister.invalidEmail');
    }
    return error;
  };

  const isNewEmail = (meEmail: string | undefined, value: string) => {
    let error;
    if (value === meEmail) {
      error = t('pages.loginRegister.sameEmail');
    }
    return error;
  };

  const isNewPhoneNumber = (mePhoneNumber: string | undefined, value: string) => {
    let error;
    if (value === mePhoneNumber) {
      error = t('pages.loginRegister.samePhoneNumber');
    }
    return error;
  };

  const validatePassword = (value: string): string | undefined => {
    if (value.length < 8) {
      setErrorLength8(true);
    } else {
      setErrorLength8(false);
    }

    if (!value.match(/[0-9]+/)) {
      setErrorNumber1(true);
    } else {
      setErrorNumber1(false);
    }

    if (!value.match(/[!@#$%^&*(),.?":{}|<>]/)) {
      setErrorSpecialChar1(true);
    } else {
      setErrorSpecialChar1(false);
    }

    return undefined;
  };

  const validateConfirmPassword = (password: string, value: string | undefined): string | undefined => {
    if (!equals(password, value || '')) {
      return t('pages.loginRegister.passwordsNotMatching');
    }
    return undefined;
  };

  return {
    maskPassword,
    maskConfirmPassword,
    checkConditions,
    errorCredentials,
    errorLength8,
    errorNumber1,
    errorSpecialChar1,
    handleMaskPassword,
    isNewEmail,
    isNewPhoneNumber,
    setCheckConditions,
    setErrorCredentials,
    handleMaskConfirmPassword,
    validateEmail,
    validateConfirmPassword,
    validatePassword,
  };
};

export default useValidationPreForm;
